// MARK://dhk8/admin-shell/pages/modules/InventoryLotsPage
// Vista de lotes (FEFO) → dhk8-erp-inventory. Cada StockLot lleva lotNumber,
// expiryDate, cantidad y almacén; la lista llega ordenada por caducidad ascendente
// (FEFO — First Expired, First Out).
//
// DS RBAC (Gustavo 2026-06-22): SummaryStrip de KPIs (activos / caducan pronto /
// caducados) + DataTable + FilterBar (búsqueda por SKU/lote + filtro por caducidad).
// Solo presentación: el subgrafo no expone una mutation para CREAR lotes a mano (se
// crean al recibir mercancía con número de lote/caducidad), así que esta vista es de
// lectura — no se fabrica un endpoint inexistente. Ver reporte.
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Boxes, CalendarClock, ChevronLeft } from 'lucide-react'
import * as inv from '../../lib/clients/inventory.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import DataTable, { type DataTableColumn, type SortState } from '../../components/DataTable.js'
import FilterBar from '../../components/FilterBar.js'
import { SummaryStrip } from '../../components/StatCard.js'
import StatusPill, { type StatusTone } from '../../components/StatusPill.js'
import { unitShort } from '../../components/UnitPicker.js'

const DAY = 86400000

function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / DAY)
}
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
}
/** Expiry urgency → pill tone + label + bucket (para el filtro). */
function expiryMeta(iso: string | null): { tone: StatusTone; label: string; bucket: string } {
  if (!iso) return { tone: 'neutral', label: 'Sin caducidad', bucket: 'none' }
  const d = daysUntil(iso)
  if (d < 0) return { tone: 'danger', label: `Caducó hace ${Math.abs(d)} d`, bucket: 'expired' }
  if (d <= 3) return { tone: 'danger', label: `Caduca en ${d} d`, bucket: 'urgent' }
  if (d <= 14) return { tone: 'warn', label: `Caduca en ${d} d`, bucket: 'soon' }
  return { tone: 'ok', label: `${d} d restantes`, bucket: 'ok' }
}

const EXPIRY_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'expired', label: 'Caducados' },
  { value: 'urgent', label: 'Caducan pronto (≤3 d)' },
  { value: 'soon', label: 'Por caducar (≤14 d)' },
  { value: 'ok', label: 'Con margen' },
  { value: 'none', label: 'Sin caducidad' },
]

export default function InventoryLotsPage() {
  const list = useAsync((s) => inv.listLots(s))
  const warehouses = useAsync((s) => inv.listWarehouses(s))

  const [search, setSearch] = useState('')
  const [expiryFilter, setExpiryFilter] = useState('')
  const [sort, setSort] = useState<SortState | undefined>(undefined)

  const whName = (id: string | null) => (id ? warehouses.data?.find((w) => w.id === id)?.name ?? '—' : '—')

  const stats = useMemo(() => {
    const all = list.data ?? []
    const urgent = all.filter((l) => l.expiryDate && daysUntil(l.expiryDate) <= 3 && daysUntil(l.expiryDate) >= 0).length
    const expired = all.filter((l) => l.expiryDate && daysUntil(l.expiryDate) < 0).length
    return { total: all.length, urgent, expired }
  }, [list.data])

  const rows = useMemo(() => {
    let r = list.data ?? []
    const q = search.trim().toLowerCase()
    if (q) r = r.filter((l) => (l.sku ?? '').toLowerCase().includes(q) || l.lotNumber.toLowerCase().includes(q))
    if (expiryFilter) r = r.filter((l) => expiryMeta(l.expiryDate).bucket === expiryFilter)
    if (sort) {
      const dir = sort.dir === 'asc' ? 1 : -1
      r = r.slice().sort((a, b) => {
        let av: number | string, bv: number | string
        switch (sort.key) {
          case 'sku': av = (a.sku ?? a.stockItemId).toLowerCase(); bv = (b.sku ?? b.stockItemId).toLowerCase(); break
          case 'expiry': av = a.expiryDate ?? '9999'; bv = b.expiryDate ?? '9999'; break
          case 'warehouse': av = whName(a.warehouseId).toLowerCase(); bv = whName(b.warehouseId).toLowerCase(); break
          case 'qty': av = a.qty; bv = b.qty; break
          default: return 0
        }
        return av < bv ? -dir : av > bv ? dir : 0
      })
    }
    return r
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, search, expiryFilter, sort, warehouses.data])

  const columns: DataTableColumn<inv.StockLot>[] = [
    {
      key: 'sku', header: 'Insumo · lote', sortable: true, render: (l) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 'var(--pos-weight-medium, 500)' }}>
          <Boxes size={14} aria-hidden style={{ opacity: 0.6 }} />{l.sku ?? l.stockItemId.slice(0, 8)} · <span className="adm-mono">{l.lotNumber}</span>
        </span>
      ),
    },
    {
      key: 'expiry', header: 'Caducidad', sortable: true, render: (l) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <CalendarClock size={12} aria-hidden style={{ opacity: 0.6 }} />{l.expiryDate ? fmtDate(l.expiryDate) : 'Sin fecha'}
        </span>
      ),
    },
    { key: 'warehouse', header: 'Almacén', sortable: true, render: (l) => whName(l.warehouseId) },
    { key: 'estado', header: 'Estado', render: (l) => { const m = expiryMeta(l.expiryDate); return <StatusPill tone={m.tone} label={m.label} /> } },
    { key: 'qty', header: 'Cantidad', align: 'right', sortable: true, render: (l) => <span className="adm-mono" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{l.qty} {l.uom ? unitShort(l.uom) : ''}</span> },
  ]

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Link className="adm-btn adm-btn--ghost adm-btn--sm" to="/inventory" aria-label="Volver a inventario"><ChevronLeft size={18} /></Link>
          <div>
            <h1 className="adm-page__title">Lotes de inventario</h1>
            <p className="adm-page__sub">Caducidades · orden FEFO (primero en caducar, primero en salir)</p>
          </div>
        </div>
      </div>

      {list.data && (
        <div style={{ marginBottom: 16 }}>
          <SummaryStrip items={[
            { label: 'Lotes activos', value: stats.total },
            { label: 'Caducan pronto (≤3 d)', value: stats.urgent, tone: stats.urgent ? 'warn' : 'neutral' },
            { label: 'Caducados', value: stats.expired, tone: stats.expired ? 'danger' : 'neutral' },
          ]} />
        </div>
      )}

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && (
        <DataTable<inv.StockLot>
          columns={columns}
          rows={rows}
          getRowKey={(l) => l.id}
          zebra
          sort={sort ?? null}
          onSortChange={setSort}
          emptyText={list.data.length === 0 ? 'Aún no hay lotes registrados. Los lotes se crean al recibir mercancía con número de lote y caducidad.' : 'Ningún lote coincide con el filtro.'}
          toolbar={<FilterBar
            search={search}
            onSearch={setSearch}
            searchPlaceholder="Buscar SKU o número de lote…"
            filters={[{ key: 'expiry', label: 'Caducidad', value: expiryFilter, onChange: setExpiryFilter, options: EXPIRY_OPTIONS }]}
          />}
        />
      )}
    </div>
  )
}
